import React from "react"
import { fetchWithLocale } from "components/utils/Api.jsx";
import PostGridCard from "components/posts/PostGridCard";
import i18n from "i18n/index.js";

function getInitialPage() {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get("page"), 10);
  return page > 0 ? page : 1;
}

const PostBody = () => {
  const [loading, setLoading] = React.useState(true)
  const [isPageloading, setIsPageloading] = React.useState(false)
  const [lastPost, setLastPost] = React.useState(null)
  const [posts, setPosts] = React.useState([])
  const [hasMore, setHasMore] = React.useState(false)
  const [totalCount, setTotalCount] = React.useState(0)
  const [mainImageLoaded, setMainImageLoaded] = React.useState(false)
  const [postsImagesLoaded, setPostsImagesLoaded] = React.useState({})
  const pageRef = React.useRef(getInitialPage())

  const fetchPosts = React.useCallback((isNewPage = false) => {
    const nextPage = isNewPage ? pageRef.current + 1 : pageRef.current;
    if (!isNewPage) {
      setLoading(true);
    } else {
      setIsPageloading(true);
    }

    return fetchWithLocale(`/posts.json?page=${nextPage}`)
      .then((res) => res.json())
      .then((data) => {
        const batch = data.data.posts || [];
        if (!isNewPage) {
          setLastPost(data.data.last_post);
          setPosts(batch);
        } else {
          setPosts((prev) => {
            const merged = [...prev, ...batch];
            return Array.from(new Map(merged.map((post) => [post.id, post])).values());
          });
        }
        setHasMore(Boolean(data.data.has_more));
        setTotalCount(typeof data.data.total_count === "number" ? data.data.total_count : batch.length);
        if (isNewPage) {
          pageRef.current = nextPage;
        }
        setLoading(false);
        setIsPageloading(false);
      })
      .catch((error) => {
        console.error("Error fetching posts:", error);
        setLoading(false);
        setIsPageloading(false);
      });
  }, []);

  React.useEffect(() => {
    fetchPosts(false);
  }, [fetchPosts]);

  React.useEffect(() => {
    setMainImageLoaded(false)
  }, [lastPost])

  React.useEffect(() => {
    if (!posts?.length) return

    setPostsImagesLoaded((prev) => {
      const next = { ...prev }
      let changed = false

      posts.forEach((post) => {
        if (post.id == null || next[post.id] !== undefined) return
        next[post.id] = false
        changed = true
      })

      return changed ? next : prev
    })
  }, [posts])

  const handleMainImageLoad = (e) => {
    e.target.classList.remove("blur")
    setMainImageLoaded(true)
  }

  const handlePostImageLoad = (postId, e) => {
    e.target.classList.remove("blur")
    setPostsImagesLoaded((prev) => {
      if (prev[postId]) return prev
      return { ...prev, [postId]: true }
    })
  }

  const handleLoadMore = () => {
    if (isPageloading || !hasMore) return;
    fetchPosts(true);
  };

  return (
    <>
      <section className="first-post-grid-container">
        {loading || !lastPost ? (
          <section className="first-post-image">
            <figure>
              <div className="oi-skeleton oi-skeleton--wave oi-w-full" style={{ height: 500 }} aria-hidden />
            </figure>
            <div className="first-post-info">
              <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: 120, height: 24, marginBottom: 12 }} aria-hidden />
              <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '90%', height: 40, marginBottom: 12 }} aria-hidden />
              <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '100%', height: 24, marginBottom: 8 }} aria-hidden />
              <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '50%', height: 20 }} aria-hidden />
            </div>
          </section>
        ) : (
          <section className="first-post-image">
            <figure>
              <a href={lastPost.url} title="View Details" target="_blank" rel="noopener noreferrer">
                <img
                  className="lazyload blur"
                  src={lastPost.cover_f || "/assets/phone.svg"}
                  data-original={lastPost.cover_f}
                  onLoad={handleMainImageLoad}
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.src = "/assets/phone.svg"
                    e.target.classList.remove("blur")
                    setMainImageLoaded(true)
                  }}
                  style={{ width: "100%" }}
                  alt={lastPost.title}
                />
                {!mainImageLoaded && (
                  <div
                    className="oi-skeleton oi-skeleton--wave oi-skeleton--cover"
                    aria-hidden
                  />
                )}
              </a>
            </figure>
            <div className="first-post-info">
              <p className="zone h7">{lastPost.label}</p>
              <h3>
                <a href={lastPost.url} title="View Details" target="_blank" rel="noopener noreferrer">
                  {lastPost.title}
                </a>
              </h3>
              <p className="subtile h4">{lastPost.subtitle}</p>
              <p className="author h7">
                <strong>{lastPost.author_name}</strong> | {lastPost.author_sm}
              </p>
            </div>
          </section>
        )}
      </section>

      <section className="posts-grid-container">
        {loading || !posts || posts.length === 0
          ? Array.from(new Array(8)).map((_, idx) => (
            <article key={idx} className="post-grid">
              <div className="post-wraper home-blog-card-stack">
                <figure style={{ position: "relative" }}>
                  <div
                    className="oi-skeleton oi-skeleton--wave oi-skeleton--rounded oi-w-full"
                    style={{ height: 255 }}
                    aria-hidden
                  />
                </figure>
                <div className="info-slide oi-flex oi-flex-column">
                  <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '60%', height: 24 }} aria-hidden />
                  <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '80%', height: 20 }} aria-hidden />
                  <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '40%', height: 18 }} aria-hidden />
                  <div className="oi-skeleton oi-skeleton--wave oi-skeleton--text" style={{ width: '50%', height: 15 }} aria-hidden />
                </div>
              </div>
            </article>
          ))
          : posts.map((post, idx) => (
            <PostGridCard
              key={post.id || idx}
              post={post}
              href={post.url}
              onImageLoad={(e) => handlePostImageLoad(post.id, e)}
              onImageError={(e) => {
                e.target.onerror = null
                e.target.src = "/assets/phone.svg"
                e.target.classList.remove("blur")
                handlePostImageLoad(post.id, e)
              }}
              imageOverlay={
                !postsImagesLoaded[post.id] ? (
                  <div
                    className="oi-skeleton oi-skeleton--wave oi-skeleton--cover oi-skeleton--rounded"
                    aria-hidden
                  />
                ) : null
              }
            />
          ))}
      </section>

      {!loading && posts.length > 0 && (totalCount > 0 || hasMore) && (
        <div className="products-load-more">
          <div
            className="products-load-more__track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={totalCount ? Math.round((posts.length / totalCount) * 100) : 0}
          >
            <div
              className="products-load-more__fill"
              style={{
                width: totalCount ? `${Math.min(100, (posts.length / totalCount) * 100)}%` : "0%",
              }}
            />
          </div>
          <p className="products-load-more__status">
            {i18n.t("blog.showing_count", {
              loaded: posts.length,
              total: totalCount || posts.length,
            })}
          </p>
          {hasMore && (
            <button
              type="button"
              className="products-load-more__button"
              disabled={isPageloading}
              aria-busy={isPageloading}
              onClick={handleLoadMore}
            >
              {isPageloading ? (
                <span className="products-load-more__loader" aria-hidden="true">
                  <span className="request-submit-dot" />
                  <span className="request-submit-dot" />
                  <span className="request-submit-dot" />
                </span>
              ) : (
                i18n.t("products_search.load_more")
              )}
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default PostBody
